import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Car, Bug, Play, Download, Users, CheckCircle, XCircle, 
  AlertTriangle, ChevronLeft, RefreshCw, CreditCard, Image, DollarSign,
  Copy, ExternalLink, Zap, FileText, LogOut, MessageSquare, Key, Lock, Unlock
} from 'lucide-react';
import { toast } from 'sonner';
import { getVehiclePhotoUrl, filterValidPhotos } from '../lib/imageUtils';
import { getParentDashboard } from '../lib/navigationUtils';

const DevDebugPanel = () => {
  const { api, user, clearAllSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [testResults, setTestResults] = useState(null);
  const [paymentTestResults, setPaymentTestResults] = useState(null);
  const [verificationTestResults, setVerificationTestResults] = useState(null);
  const [vehicleVisibilityResults, setVehicleVisibilityResults] = useState(null);
  const [eventLog, setEventLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentTesting, setPaymentTesting] = useState(false);
  const [verificationTesting, setVerificationTesting] = useState(false);
  const [vehicleVisibilityTesting, setVehicleVisibilityTesting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  
  // P0 Safety Tests state
  const [regoTestResults, setRegoTestResults] = useState(null);
  const [regoTesting, setRegoTesting] = useState(false);
  const [cancellationTestResults, setCancellationTestResults] = useState(null);
  const [cancellationTesting, setCancellationTesting] = useState(false);
  const [disputeTestResults, setDisputeTestResults] = useState(null);
  const [disputeTesting, setDisputeTesting] = useState(false);
  
  // P0: Stored Payment Authorization Tests state
  const [storedPaymentAuthResults, setStoredPaymentAuthResults] = useState(null);
  const [storedPaymentAuthTesting, setStoredPaymentAuthTesting] = useState(false);
  
  // P0 Part 2: Post-Hire Charges Tests state
  const [postHireChargesResults, setPostHireChargesResults] = useState(null);
  const [postHireChargesTesting, setPostHireChargesTesting] = useState(false);
  
  // P0 Part 3: Admin Review Layer Tests state
  const [adminReviewResults, setAdminReviewResults] = useState(null);
  const [adminReviewTesting, setAdminReviewTesting] = useState(false);
  
  // P0 Part 4: Stripe Charge Execution Tests state
  const [stripeChargeResults, setStripeChargeResults] = useState(null);
  const [stripeChargeTesting, setStripeChargeTesting] = useState(false);

  // P0 Part 5: Retry & Escalation System Tests state
  const [retryEscalationResults, setRetryEscalationResults] = useState(null);
  const [retryEscalationTesting, setRetryEscalationTesting] = useState(false);

  // P0 Part 6: Dispute Interaction System Tests state
  const [disputeFreezeResults, setDisputeFreezeResults] = useState(null);
  const [disputeFreezeTesting, setDisputeFreezeTesting] = useState(false);

  // P0 Part 7: Financial Enforcement Dashboard Tests state
  const [financialDashboardResults, setFinancialDashboardResults] = useState(null);
  const [financialDashboardTesting, setFinancialDashboardTesting] = useState(false);

  // P0 Part 8: E2E Full Lifecycle Tests state
  const [e2eLifecycleResults, setE2eLifecycleResults] = useState(null);
  const [e2eLifecycleTesting, setE2eLifecycleTesting] = useState(false);

  // P0 Overdue Protection System Tests state
  const [overdueResults, setOverdueResults] = useState(null);
  const [overdueTesting, setOverdueTesting] = useState(false);

  // P0 Legal Re-Acceptance System Tests state
  const [legalReacceptanceResults, setLegalReacceptanceResults] = useState(null);
  const [legalReacceptanceTesting, setLegalReacceptanceTesting] = useState(false);

  // P1 Extension Flow System Tests state
  const [extensionFlowResults, setExtensionFlowResults] = useState(null);
  const [extensionFlowTesting, setExtensionFlowTesting] = useState(false);

  // Messaging Smoke Test state
  const [messagingTestResults, setMessagingTestResults] = useState(null);
  const [messagingTesting, setMessagingTesting] = useState(false);

  // P0: Booking Admin Tools state
  const [bookingIdInput, setBookingIdInput] = useState('');
  const [bookingAdminLoading, setBookingAdminLoading] = useState(false);
  const [bookingAdminResult, setBookingAdminResult] = useState(null);

  // P0: Unlock/Lock Return Tools state
  const [unlockLockBookingId, setUnlockLockBookingId] = useState('');
  const [unlockLockLoading, setUnlockLockLoading] = useState(false);
  const [unlockLockResult, setUnlockLockResult] = useState(null);

  // Confirmation Dialog state for dangerous actions
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    description: '',
    action: null,
    destructive: false
  });

  const showConfirmation = (title, description, action, destructive = false) => {
    setConfirmDialog({
      open: true,
      title,
      description,
      action,
      destructive
    });
  };

  const handleConfirm = () => {
    if (confirmDialog.action) {
      confirmDialog.action();
    }
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  useEffect(() => {
    fetchEventLog();
  }, []);

  const fetchEventLog = async () => {
    try {
      const res = await api.get('/dev-debug/event-log');
      setEventLog(res.data.events || []);
    } catch (error) {
      console.error('Error fetching event log:', error);
    }
  };

  const runSelfTests = async () => {
    setLoading(true);
    try {
      const res = await api.get('/dev-debug/self-tests');
      setTestResults(res.data);
      toast.success('Self tests completed');
    } catch (error) {
      toast.error('Failed to run self tests');
    } finally {
      setLoading(false);
    }
  };

  const runPaymentFlowTest = async () => {
    setPaymentTesting(true);
    setPaymentTestResults(null);
    try {
      const res = await api.post('/dev-debug/payment-flow-test');
      setPaymentTestResults(res.data);
      if (res.data.summary?.success) {
        toast.success('Payment flow test passed!');
      } else {
        toast.error('Payment flow test had failures');
      }
    } catch (error) {
      toast.error('Failed to run payment flow test');
    } finally {
      setPaymentTesting(false);
    }
  };

  const runVerificationSmokeTest = async () => {
    setVerificationTesting(true);
    setVerificationTestResults(null);
    try {
      // Fetch users
      const usersRes = await api.get('/admin/users');
      const users = usersRes.data.users || [];
      
      // Fetch verifications
      const verificationsRes = await api.get('/admin/verifications');
      const pendingVerifications = verificationsRes.data.verifications?.filter(v => v.status === 'pending') || [];
      
      // Fetch audit logs (verification-related)
      const auditRes = await api.get('/platform-owner/audit-logs?limit=100');
      const verificationLogs = (auditRes.data.logs || []).filter(log => 
        log.action?.includes('verification') || log.entity_type === 'verification'
      ).slice(0, 10);
      
      // First 3 users
      const first3Users = users.slice(0, 3).map(u => ({
        email: u.email,
        verification_status: u.verification_status || 'unverified',
        full_name: u.full_name
      }));
      
      setVerificationTestResults({
        total_users: users.length,
        pending_verifications: pendingVerifications.length,
        verified_users: users.filter(u => u.verification_status === 'verified').length,
        first_3_users: first3Users,
        verification_audit_logs: verificationLogs,
        fetched_at: new Date().toISOString()
      });
      
      toast.success('Verification smoke test completed');
    } catch (error) {
      console.error('Verification test error:', error);
      toast.error('Failed to run verification smoke test');
    } finally {
      setVerificationTesting(false);
    }
  };

  const runVehicleVisibilityTest = async () => {
    setVehicleVisibilityTesting(true);
    setVehicleVisibilityResults(null);
    try {
      // Fetch public vehicles (Browse Vehicles)
      const publicRes = await api.get('/vehicles');
      const publicVehicles = publicRes.data.vehicles || [];
      
      // Fetch my vehicles (Owner's vehicles)
      let myVehicles = [];
      try {
        const myRes = await api.get('/vehicles/my-vehicles');
        myVehicles = myRes.data.vehicles || [];
      } catch (e) {
        // User may not be an owner
      }
      
      // Get the last created vehicle from public list
      const lastCreatedVehicle = publicVehicles.length > 0 
        ? publicVehicles.reduce((latest, v) => 
            new Date(v.created_at) > new Date(latest.created_at) ? v : latest
          )
        : null;
      
      // Test photo URL resolution for vehicles with photos
      const photoTests = [];
      for (const v of publicVehicles.slice(0, 3)) {
        const validPhotos = filterValidPhotos(v.photos);
        if (validPhotos.length > 0) {
          const photoUrl = getVehiclePhotoUrl(validPhotos[0]);
          photoTests.push({
            vehicle_id: v.vehicle_id,
            make_model: `${v.make} ${v.model}`,
            s3_key: validPhotos[0],
            resolved_url: photoUrl,
            photo_count: validPhotos.length
          });
        }
      }
      
      setVehicleVisibilityResults({
        public_vehicle_count: publicRes.data.total || publicVehicles.length,
        my_vehicle_count: myVehicles.length,
        last_created_vehicle: lastCreatedVehicle ? {
          vehicle_id: lastCreatedVehicle.vehicle_id,
          owner_id: lastCreatedVehicle.owner_id,
          make_model: `${lastCreatedVehicle.make} ${lastCreatedVehicle.model}`,
          status: lastCreatedVehicle.status,
          created_at: lastCreatedVehicle.created_at,
          photos: filterValidPhotos(lastCreatedVehicle.photos)
        } : null,
        photo_url_tests: photoTests,
        in_public_list: lastCreatedVehicle ? publicVehicles.some(v => v.vehicle_id === lastCreatedVehicle.vehicle_id) : false,
        in_my_list: lastCreatedVehicle ? myVehicles.some(v => v.vehicle_id === lastCreatedVehicle.vehicle_id) : false,
        fetched_at: new Date().toISOString()
      });
      
      toast.success('Vehicle visibility test completed');
    } catch (error) {
      console.error('Vehicle visibility test error:', error);
      toast.error('Failed to run vehicle visibility test');
    } finally {
      setVehicleVisibilityTesting(false);
    }
  };

  const exportDebugPack = async () => {
    try {
      const res = await api.get('/dev-debug/export');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `debug-pack-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast.success('Debug pack exported');
    } catch (error) {
      toast.error('Failed to export debug pack');
    }
  };

  const seedTestUsers = async () => {
    setSeeding(true);
    try {
      const res = await api.post('/dev-debug/seed-test-users');
      toast.success('Test users created');
      res.data.users.forEach(u => {
        toast.info(`${u.email} / ${u.password} (${u.roles.join(', ')})`);
      });
    } catch (error) {
      toast.error('Failed to seed test users');
    } finally {
      setSeeding(false);
    }
  };

  // P0: Rego Expiry Enforcement Test
  const runRegoExpiryTest = async () => {
    setRegoTesting(true);
    setRegoTestResults(null);
    try {
      const res = await api.post('/dev-debug/test-rego-enforcement');
      // Transform the response to match our display format
      const tests = res.data.tests?.map(t => ({
        name: t.test || t.vehicle,
        passed: t.passed,
        details: t.passed ? 'PASS' : `Expected ${t.expected}, Got ${t.actual}`
      })) || [];
      const allPassed = tests.every(t => t.passed);
      setRegoTestResults({
        all_passed: allPassed,
        tests
      });
      toast.success(allPassed ? 'Rego enforcement tests PASSED' : 'Some tests failed');
    } catch (error) {
      toast.error('Failed to run rego tests');
      setRegoTestResults({ all_passed: false, error: error.message, tests: [] });
    } finally {
      setRegoTesting(false);
    }
  };

  // P0: Cancellation Policy Test
  const runCancellationPolicyTest = async () => {
    setCancellationTesting(true);
    setCancellationTestResults(null);
    try {
      const res = await api.post('/dev-debug/test-cancellation-math');
      // Transform the response to match our display format
      const tests = res.data.test_cases?.map(tc => ({
        name: `${tc.policy.toUpperCase()} @ ${tc.days_until_start} days`,
        passed: tc.passed,
        details: `Expected ${tc.expected_refund_pct}%, Got ${tc.actual_refund_pct}% ($${tc.refund_amount})`
      })) || [];
      setCancellationTestResults({
        all_passed: res.data.summary?.all_passed,
        tests
      });
      toast.success(res.data.summary?.all_passed ? 'Cancellation policy tests PASSED' : 'Some tests failed');
    } catch (error) {
      toast.error('Failed to run cancellation tests');
      setCancellationTestResults({ all_passed: false, error: error.message, tests: [] });
    } finally {
      setCancellationTesting(false);
    }
  };

  // P0: Dispute Workflow Test
  const runDisputeWorkflowTest = async () => {
    setDisputeTesting(true);
    setDisputeTestResults(null);
    try {
      const res = await api.post('/dev-debug/test-dispute-workflow');
      // Transform the response to match our display format
      const tests = res.data.steps?.map(s => ({
        name: s.step,
        passed: s.status === 'passed',
        details: s.status.toUpperCase()
      })) || [];
      setDisputeTestResults({
        all_passed: res.data.summary?.all_passed,
        tests
      });
      toast.success(res.data.summary?.all_passed ? 'Dispute workflow tests PASSED' : 'Some tests failed');
    } catch (error) {
      toast.error('Failed to run dispute tests');
      setDisputeTestResults({ all_passed: false, error: error.message, tests: [] });
    } finally {
      setDisputeTesting(false);
    }
  };

  // P0: Stored Payment Authorization Test
  const runStoredPaymentAuthTest = async () => {
    setStoredPaymentAuthTesting(true);
    setStoredPaymentAuthResults(null);
    try {
      const res = await api.post('/dev-debug/test-stored-payment-authorization');
      // Transform the response to match our display format
      const tests = res.data.steps?.map(s => ({
        name: s.step,
        passed: s.status === 'passed',
        details: s.details || s.status.toUpperCase()
      })) || [];
      setStoredPaymentAuthResults({
        all_passed: res.data.summary?.all_passed,
        tests,
        summary: res.data.summary
      });
      toast.success(res.data.summary?.all_passed ? 'Stored Payment Auth tests PASSED' : 'Some tests failed');
    } catch (error) {
      toast.error('Failed to run stored payment auth tests');
      setStoredPaymentAuthResults({ all_passed: false, error: error.message, tests: [] });
    } finally {
      setStoredPaymentAuthTesting(false);
    }
  };

  // P0 Part 2: Post-Hire Charges Test
  const runPostHireChargesTest = async () => {
    setPostHireChargesTesting(true);
    setPostHireChargesResults(null);
    try {
      const res = await api.post('/dev-debug/test-post-hire-charges');
      const tests = res.data.steps?.map(s => ({
        name: s.step,
        passed: s.status === 'passed',
        details: s.details || s.status.toUpperCase()
      })) || [];
      setPostHireChargesResults({
        all_passed: res.data.summary?.all_passed,
        tests,
        summary: res.data.summary
      });
      toast.success(res.data.summary?.all_passed ? 'Post-Hire Charges tests PASSED' : 'Some tests failed');
    } catch (error) {
      toast.error('Failed to run post-hire charges tests');
      setPostHireChargesResults({ all_passed: false, error: error.message, tests: [] });
    } finally {
      setPostHireChargesTesting(false);
    }
  };

  // P0 Part 3: Admin Review Layer test
  const runAdminReviewTest = async () => {
    setAdminReviewTesting(true);
    setAdminReviewResults(null);
    try {
      const res = await api.post('/dev-debug/test-admin-review-layer');
      const tests = res.data.steps?.map(s => ({
        name: s.step,
        passed: s.status === 'passed',
        details: s.details || s.status.toUpperCase()
      })) || [];
      setAdminReviewResults({
        all_passed: res.data.summary?.all_passed,
        tests,
        summary: res.data.summary
      });
      toast.success(res.data.summary?.all_passed ? 'Admin Review Layer tests PASSED' : 'Some tests failed');
    } catch (error) {
      toast.error('Failed to run admin review layer tests');
      setAdminReviewResults({ all_passed: false, error: error.message, tests: [] });
    } finally {
      setAdminReviewTesting(false);
    }
  };

  // P0 Part 4: Stripe Charge Execution test
  const runStripeChargeTest = async () => {
    setStripeChargeTesting(true);
    setStripeChargeResults(null);
    try {
      const res = await api.post('/dev-debug/test-stripe-charge-execution');
      const tests = res.data.steps?.map(s => ({
        name: s.step,
        passed: s.status === 'passed',
        details: s.details || s.status.toUpperCase(),
        skipped: s.status === 'skipped'
      })) || [];
      setStripeChargeResults({
        all_passed: res.data.summary?.all_passed,
        tests,
        summary: res.data.summary,
        proof_summary: res.data.proof_summary
      });
      toast.success(res.data.summary?.all_passed ? 'Stripe Charge Execution tests PASSED' : 'Some tests failed');
    } catch (error) {
      toast.error('Failed to run Stripe charge execution tests');
      setStripeChargeResults({ all_passed: false, error: error.message, tests: [] });
    } finally {
      setStripeChargeTesting(false);
    }
  };

  // P0 Part 5: Retry & Escalation System test
  const runRetryEscalationTest = async () => {
    setRetryEscalationTesting(true);
    setRetryEscalationResults(null);
    try {
      const res = await api.post('/dev-debug/test-retry-escalation-system');
      const tests = res.data.steps?.map(s => ({
        name: s.step,
        passed: s.status === 'passed',
        details: s.details || s.status.toUpperCase(),
        skipped: s.status === 'skipped'
      })) || [];
      setRetryEscalationResults({
        all_passed: res.data.summary?.all_passed,
        tests,
        summary: res.data.summary,
        proof_summary: res.data.proof_summary
      });
      toast.success(res.data.summary?.all_passed ? 'Retry & Escalation tests PASSED' : 'Some tests failed');
    } catch (error) {
      toast.error('Failed to run Retry & Escalation tests');
      setRetryEscalationResults({ all_passed: false, error: error.message, tests: [] });
    } finally {
      setRetryEscalationTesting(false);
    }
  };

  // P0 Part 6: Dispute Freeze System test
  const runDisputeFreezeTest = async () => {
    setDisputeFreezeTesting(true);
    setDisputeFreezeResults(null);
    try {
      const res = await api.post('/dev-debug/test-dispute-freeze-system');
      const tests = res.data.steps?.map(s => ({
        name: s.step,
        passed: s.status === 'passed',
        details: s.details || s.status.toUpperCase(),
        skipped: s.status === 'skipped'
      })) || [];
      setDisputeFreezeResults({
        all_passed: res.data.summary?.all_passed,
        tests,
        summary: res.data.summary,
        proof_summary: res.data.proof_summary
      });
      toast.success(res.data.summary?.all_passed ? 'Dispute Freeze tests PASSED' : 'Some tests failed');
    } catch (error) {
      toast.error('Failed to run Dispute Freeze tests');
      setDisputeFreezeResults({ all_passed: false, error: error.message, tests: [] });
    } finally {
      setDisputeFreezeTesting(false);
    }
  };

  // P0 Part 7: Financial Enforcement Dashboard test
  const runFinancialDashboardTest = async () => {
    setFinancialDashboardTesting(true);
    setFinancialDashboardResults(null);
    try {
      const res = await api.post('/dev-debug/test-financial-dashboard');
      const tests = res.data.steps?.map(s => ({
        name: s.step,
        passed: s.status === 'passed',
        details: s.details || s.status.toUpperCase(),
        skipped: s.status === 'skipped'
      })) || [];
      setFinancialDashboardResults({
        all_passed: res.data.summary?.all_passed,
        tests,
        summary: res.data.summary,
        proof_summary: res.data.proof_summary
      });
      toast.success(res.data.summary?.all_passed ? 'Financial Dashboard tests PASSED' : 'Some tests failed');
    } catch (error) {
      toast.error('Failed to run Financial Dashboard tests');
      setFinancialDashboardResults({ all_passed: false, error: error.message, tests: [] });
    } finally {
      setFinancialDashboardTesting(false);
    }
  };

  // P0 Part 8: E2E Full Lifecycle Test
  const runE2eLifecycleTest = async () => {
    setE2eLifecycleTesting(true);
    setE2eLifecycleResults(null);
    try {
      const res = await api.post('/dev-debug/test-e2e-full-lifecycle');
      const tests = res.data.steps?.map(s => ({
        name: s.step,
        passed: s.status === 'passed' || s.status?.includes('passed'),
        details: s.details || s.status?.toUpperCase() || 'N/A',
        skipped: s.status === 'skipped'
      })) || [];
      setE2eLifecycleResults({
        all_passed: res.data.summary?.all_passed,
        tests,
        summary: res.data.summary,
        proof_summary: res.data.proof_summary,
        key_ids: res.data.key_ids,
        audit_event_counts: res.data.audit_event_counts,
        csv_exports: res.data.csv_exports,
        cleanup: res.data.cleanup,
        links: res.data.links,
        test_id: res.data.test_id
      });
      toast.success(res.data.summary?.all_passed ? '🎉 E2E Full Lifecycle Test PASSED!' : 'Some tests failed');
    } catch (error) {
      toast.error('Failed to run E2E Lifecycle test');
      setE2eLifecycleResults({ all_passed: false, error: error.message, tests: [] });
    } finally {
      setE2eLifecycleTesting(false);
    }
  };

  // Copy report to clipboard
  const copyE2eReport = () => {
    if (!e2eLifecycleResults) return;
    const report = JSON.stringify(e2eLifecycleResults, null, 2);
    navigator.clipboard.writeText(report);
    toast.success('Report copied to clipboard!');
  };

  // P0 Overdue Protection test
  const runOverdueProtectionTest = async () => {
    setOverdueTesting(true);
    setOverdueResults(null);
    try {
      const res = await api.post('/dev-debug/test-overdue-protection');
      const tests = res.data.steps?.map(s => ({
        name: s.step,
        passed: s.status === 'passed',
        details: s.details || s.status?.toUpperCase() || 'N/A',
        skipped: s.status === 'skipped'
      })) || [];
      setOverdueResults({
        all_passed: res.data.summary?.all_passed,
        tests,
        summary: res.data.summary,
        proof_summary: res.data.proof_summary,
        key_ids: res.data.key_ids,
        audit_event_count: res.data.audit_event_count,
        cleanup: res.data.cleanup,
        test_id: res.data.test_id
      });
      toast.success(res.data.summary?.all_passed ? 'Overdue Protection Test PASSED!' : 'Some tests failed');
    } catch (error) {
      toast.error('Failed to run Overdue Protection test');
      setOverdueResults({ all_passed: false, error: error.message, tests: [] });
    } finally {
      setOverdueTesting(false);
    }
  };

  // P0 Legal Re-Acceptance System test
  const runLegalReacceptanceTest = async () => {
    setLegalReacceptanceTesting(true);
    setLegalReacceptanceResults(null);
    try {
      const res = await api.post('/dev-debug/test-legal-re-acceptance');
      const tests = res.data.steps?.map(s => ({
        name: s.step,
        passed: s.status === 'passed',
        details: s.details || s.status?.toUpperCase() || 'N/A',
        skipped: s.status === 'skipped'
      })) || [];
      setLegalReacceptanceResults({
        all_passed: res.data.summary?.all_passed,
        tests,
        summary: res.data.summary,
        proof_summary: res.data.proof_summary,
        key_ids: res.data.key_ids,
        cleanup: res.data.cleanup,
        test_id: res.data.test_id
      });
      toast.success(res.data.summary?.all_passed ? 'Legal Re-Acceptance Test PASSED!' : 'Some tests failed');
    } catch (error) {
      toast.error('Failed to run Legal Re-Acceptance test');
      setLegalReacceptanceResults({ all_passed: false, error: error.message, tests: [] });
    } finally {
      setLegalReacceptanceTesting(false);
    }
  };

  // P1: Extension Flow Test
  const runExtensionFlowTest = async () => {
    setExtensionFlowTesting(true);
    setExtensionFlowResults(null);
    try {
      const res = await api.post('/dev-debug/test-extension-flow');
      const tests = res.data.steps?.map(s => ({
        name: s.step,
        passed: s.status === 'passed',
        details: s.details || s.status?.toUpperCase() || 'N/A',
        skipped: s.status === 'skipped'
      })) || [];
      setExtensionFlowResults({
        all_passed: res.data.summary?.all_passed,
        tests,
        summary: res.data.summary,
        proof_summary: res.data.proof_summary,
        key_ids: res.data.key_ids,
        cleanup: res.data.cleanup,
        test_id: res.data.test_id
      });
      toast.success(res.data.summary?.all_passed ? 'Extension Flow Test PASSED!' : 'Some tests failed - see details');
    } catch (error) {
      toast.error('Failed to run Extension Flow test');
      setExtensionFlowResults({ all_passed: false, error: error.message, tests: [] });
    } finally {
      setExtensionFlowTesting(false);
    }
  };

  // Clear Session (Force Logout)
  const handleClearSession = () => {
    toast.info('Clearing all session data...');
    clearAllSession();
  };

  // Messaging Smoke Test
  const runMessagingSmokeTest = async () => {
    setMessagingTesting(true);
    setMessagingTestResults(null);
    try {
      const res = await api.post('/dev-debug/test-messaging-smoke');
      setMessagingTestResults(res.data);
      if (res.data.success) {
        toast.success('Messaging Smoke Test PASSED!');
      } else {
        toast.error('Messaging test failed - see details');
      }
    } catch (error) {
      toast.error('Failed to run messaging test');
      setMessagingTestResults({ success: false, error: error.message, steps: [] });
    } finally {
      setMessagingTesting(false);
    }
  };

  // P0: Unlock Return Checklist (allows early return before trip end date)
  const handleUnlockReturn = async () => {
    if (!unlockLockBookingId.trim()) {
      toast.error('Please enter a booking ID');
      return;
    }
    setUnlockLockLoading(true);
    setUnlockLockResult(null);
    try {
      const res = await api.post(`/admin/bookings/${unlockLockBookingId.trim()}/unlock_return`, {
        reason: 'Manual unlock by platform owner via DEV DEBUG panel'
      });
      setUnlockLockResult({ success: true, action: 'unlock', message: 'Return checklist unlocked successfully' });
      toast.success('Return checklist unlocked');
    } catch (error) {
      const errMsg = error.response?.data?.detail || error.message || 'Failed to unlock return';
      setUnlockLockResult({ success: false, action: 'unlock', message: errMsg });
      toast.error(errMsg);
    } finally {
      setUnlockLockLoading(false);
    }
  };

  // P0: Lock Return Checklist (re-enables time-gate)
  const handleLockReturn = async () => {
    if (!unlockLockBookingId.trim()) {
      toast.error('Please enter a booking ID');
      return;
    }
    setUnlockLockLoading(true);
    setUnlockLockResult(null);
    try {
      const res = await api.post(`/admin/bookings/${unlockLockBookingId.trim()}/lock_return`);
      setUnlockLockResult({ success: true, action: 'lock', message: 'Return checklist re-locked successfully' });
      toast.success('Return checklist re-locked');
    } catch (error) {
      const errMsg = error.response?.data?.detail || error.message || 'Failed to lock return';
      setUnlockLockResult({ success: false, action: 'lock', message: errMsg });
      toast.error(errMsg);
    } finally {
      setUnlockLockLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate(getParentDashboard(location.pathname, user))} 
              className="text-white"
              data-testid="dev-debug-back-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Bug className="w-6 h-6 text-green-400" />
              <h1 className="font-heading text-xl font-semibold">FLIGHT RECORDER</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`${
              process.env.NODE_ENV === 'production' 
                ? 'bg-red-500/20 text-red-400 border-red-500/50' 
                : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50'
            }`}>
              {process.env.NODE_ENV === 'production' ? 'PRODUCTION' : 'PREVIEW'}
            </Badge>
            <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
              {user?.roles?.includes('platform_owner') ? 'PLATFORM OWNER' : 'SUPER ADMIN'}
            </Badge>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-8">
        {/* Quick Actions */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 sm:p-6">
              <Button 
                onClick={runSelfTests} 
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 h-10 sm:h-12 text-xs sm:text-sm"
                data-testid="run-tests-btn"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Self Tests
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 sm:p-6">
              <Button 
                onClick={runPaymentFlowTest} 
                disabled={paymentTesting}
                className="w-full bg-purple-600 hover:bg-purple-700 h-10 sm:h-12 text-xs sm:text-sm"
                data-testid="payment-test-btn"
              >
                {paymentTesting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Payment Test
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 sm:p-6">
              <Button 
                onClick={runVerificationSmokeTest} 
                disabled={verificationTesting}
                className="w-full bg-orange-600 hover:bg-orange-700 h-10 sm:h-12 text-xs sm:text-sm"
                data-testid="verification-test-btn"
              >
                {verificationTesting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Users className="w-4 h-4 mr-2" />
                )}
                Verify Test
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 sm:p-6">
              <Button 
                onClick={runVehicleVisibilityTest} 
                disabled={vehicleVisibilityTesting}
                className="w-full bg-blue-600 hover:bg-blue-700 h-10 sm:h-12 text-xs sm:text-sm"
                data-testid="vehicle-visibility-test-btn"
              >
                {vehicleVisibilityTesting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Car className="w-4 h-4 mr-2" />
                )}
                Vehicle Test
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 sm:p-6">
              <Button 
                onClick={exportDebugPack}
                variant="outline" 
                className="w-full border-slate-600 text-white hover:bg-slate-700 h-10 sm:h-12 text-xs sm:text-sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 sm:p-6">
              <Button 
                onClick={() => showConfirmation(
                  'Seed Test Users?',
                  'This will create test users in the database. Only use in non-production environments.',
                  seedTestUsers,
                  false
                )}
                disabled={seeding}
                variant="outline" 
                className="w-full border-yellow-600 text-yellow-400 hover:bg-yellow-600/20 h-10 sm:h-12 text-xs sm:text-sm"
                data-testid="seed-users-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                {seeding ? 'Seeding...' : 'Seed Users'}
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700 col-span-2 lg:col-span-1">
            <CardContent className="p-4 sm:p-6">
              <Button 
                onClick={fetchEventLog}
                variant="outline" 
                className="w-full border-slate-600 text-white hover:bg-slate-700 h-10 sm:h-12 text-xs sm:text-sm"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Log
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Session & Messaging Tools */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 sm:p-6">
              <Button 
                onClick={() => showConfirmation(
                  'Clear Session?',
                  'This will force logout and clear all browser storage. You will need to login again.',
                  handleClearSession,
                  true
                )}
                variant="destructive"
                className="w-full h-10 sm:h-12 text-xs sm:text-sm"
                data-testid="clear-session-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Clear Session (Force Logout)
              </Button>
              <p className="text-xs text-slate-500 mt-2">
                Clears all localStorage/sessionStorage and redirects to login
              </p>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 sm:p-6">
              <Button 
                onClick={runMessagingSmokeTest}
                disabled={messagingTesting}
                className="w-full bg-indigo-600 hover:bg-indigo-700 h-10 sm:h-12 text-xs sm:text-sm"
                data-testid="messaging-test-btn"
              >
                {messagingTesting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <MessageSquare className="w-4 h-4 mr-2" />
                )}
                Messaging Smoke Test
              </Button>
              <p className="text-xs text-slate-500 mt-2">
                Creates test conversation and verifies message delivery
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Messaging Test Results */}
        {messagingTestResults && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="p-4 sm:p-6 pb-2">
              <CardTitle className="font-heading text-sm sm:text-base flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-indigo-400" />
                Messaging Smoke Test Results
                {messagingTestResults.success ? (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/50 ml-2">PASSED</Badge>
                ) : (
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/50 ml-2">FAILED</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-2 sm:p-6 sm:pt-2">
              <div className="space-y-2">
                {messagingTestResults.steps?.map((step, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs sm:text-sm">
                    {step.status === 'passed' ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : step.status === 'failed' ? (
                      <XCircle className="w-4 h-4 text-red-400" />
                    ) : (
                      <RefreshCw className="w-4 h-4 text-slate-400" />
                    )}
                    <span className="text-slate-300">{step.step}</span>
                    <span className="text-slate-500 text-xs ml-auto">{step.details}</span>
                  </div>
                ))}
                {messagingTestResults.thread_id && (
                  <div className="mt-3 p-2 bg-slate-900 rounded text-xs">
                    <span className="text-slate-500">Thread ID: </span>
                    <span className="text-indigo-400">{messagingTestResults.thread_id}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* P0: Booking Admin Tools - Unlock/Lock Return Checklist */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="p-4 sm:p-6 pb-2">
            <CardTitle className="font-heading text-sm sm:text-base flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-400" />
              Booking Admin Tools
            </CardTitle>
            <CardDescription className="text-slate-400 text-xs sm:text-sm">
              Allow early return or re-lock the return time-gate for a specific booking
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-2 sm:p-6 sm:pt-2">
            <div className="space-y-4">
              {/* Booking ID Input */}
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2">Booking ID</label>
                <input
                  type="text"
                  value={unlockLockBookingId}
                  onChange={(e) => setUnlockLockBookingId(e.target.value)}
                  placeholder="Enter booking ID (e.g., bk_abc123...)"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-amber-500"
                  data-testid="unlock-lock-booking-id-input"
                />
              </div>
              
              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={handleUnlockReturn}
                  disabled={unlockLockLoading || !unlockLockBookingId.trim()}
                  className="bg-amber-600 hover:bg-amber-700 h-10 text-xs sm:text-sm"
                  data-testid="dev-unlock-return-btn"
                >
                  {unlockLockLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Unlock className="w-4 h-4 mr-2" />
                  )}
                  Allow Early Return
                </Button>
                <Button
                  onClick={handleLockReturn}
                  disabled={unlockLockLoading || !unlockLockBookingId.trim()}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-700 h-10 text-xs sm:text-sm"
                  data-testid="dev-lock-return-btn"
                >
                  {unlockLockLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Lock className="w-4 h-4 mr-2" />
                  )}
                  Re-lock Return
                </Button>
              </div>

              {/* Result Display */}
              {unlockLockResult && (
                <div className={`p-3 rounded-lg ${
                  unlockLockResult.success 
                    ? 'bg-green-500/10 border border-green-500/30' 
                    : 'bg-red-500/10 border border-red-500/30'
                }`}>
                  <div className="flex items-center gap-2">
                    {unlockLockResult.success ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400" />
                    )}
                    <span className={`text-sm ${unlockLockResult.success ? 'text-green-400' : 'text-red-400'}`}>
                      {unlockLockResult.message}
                    </span>
                  </div>
                </div>
              )}

              {/* Info */}
              <div className="text-xs text-slate-500 mt-2">
                <p><strong>Allow Early Return:</strong> Bypasses the trip end-date time-gate so the host can start the post-hire checklist early (for testing).</p>
                <p className="mt-1"><strong>Re-lock Return:</strong> Re-enables the time-gate so the "Start Return Checklist" button hides until the trip's scheduled end date.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* P0 Safety Controls Test Section */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="p-4 sm:p-6 pb-2">
            <CardTitle className="font-heading text-sm sm:text-base flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              P0 Safety Controls Tests
            </CardTitle>
            <CardDescription className="text-slate-400 text-xs sm:text-sm">
              Test Rego Enforcement, Cancellation Policy, Dispute Workflow, Payment Auth, and Post-Hire Charges
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-2 sm:p-6 sm:pt-2">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <Button 
                onClick={runRegoExpiryTest}
                disabled={regoTesting}
                className="bg-red-600 hover:bg-red-700 h-10 sm:h-12 text-xs sm:text-sm"
                data-testid="rego-test-btn"
              >
                {regoTesting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Car className="w-4 h-4 mr-2" />
                )}
                Rego Test
              </Button>
              <Button 
                onClick={runCancellationPolicyTest}
                disabled={cancellationTesting}
                className="bg-yellow-600 hover:bg-yellow-700 h-10 sm:h-12 text-xs sm:text-sm"
                data-testid="cancellation-test-btn"
              >
                {cancellationTesting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Cancellation Test
              </Button>
              <Button 
                onClick={runDisputeWorkflowTest}
                disabled={disputeTesting}
                className="bg-purple-600 hover:bg-purple-700 h-10 sm:h-12 text-xs sm:text-sm"
                data-testid="dispute-test-btn"
              >
                {disputeTesting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <AlertTriangle className="w-4 h-4 mr-2" />
                )}
                Dispute Test
              </Button>
              <Button 
                onClick={runStoredPaymentAuthTest}
                disabled={storedPaymentAuthTesting}
                className="bg-cyan-600 hover:bg-cyan-700 h-10 sm:h-12 text-xs sm:text-sm"
                data-testid="stored-payment-auth-test-btn"
                title="Verifies SetupIntent flow for storing renter payment methods securely for future charges"
              >
                {storedPaymentAuthTesting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Stored Auth
              </Button>
              <Button 
                onClick={runPostHireChargesTest}
                disabled={postHireChargesTesting}
                className="bg-amber-600 hover:bg-amber-700 h-10 sm:h-12 text-xs sm:text-sm"
                data-testid="post-hire-charges-test-btn"
                title="Tests Owner ability to submit toll, fuel, and damage charges after trip completion"
              >
                {postHireChargesTesting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <DollarSign className="w-4 h-4 mr-2" />
                )}
                Post-Hire
              </Button>
              <Button 
                onClick={runAdminReviewTest}
                disabled={adminReviewTesting}
                className="bg-indigo-600 hover:bg-indigo-700 h-10 sm:h-12 text-xs sm:text-sm"
                data-testid="admin-review-test-btn"
                title="Tests Admin workflow for approving or rejecting individual charge items before execution"
              >
                {adminReviewTesting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Approval
              </Button>
              <Button 
                onClick={runStripeChargeTest}
                disabled={stripeChargeTesting}
                className="bg-emerald-600 hover:bg-emerald-700 h-10 sm:h-12 text-xs sm:text-sm"
                data-testid="stripe-charge-test-btn"
                title="Executes real Stripe off-session charge against stored payment method for approved charges"
              >
                {stripeChargeTesting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Stripe Exec
              </Button>
              <Button 
                onClick={runRetryEscalationTest}
                disabled={retryEscalationTesting}
                className="bg-amber-600 hover:bg-amber-700 h-10 sm:h-12 text-xs sm:text-sm"
                data-testid="retry-escalation-test-btn"
                title="Simulates payment failure, automated retries (+24h, +72h, +7d), and user suspension escalation"
              >
                {retryEscalationTesting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Retry/Escalate
              </Button>
              <Button 
                onClick={runDisputeFreezeTest}
                disabled={disputeFreezeTesting}
                className="bg-pink-600 hover:bg-pink-700 h-10 sm:h-12 text-xs sm:text-sm"
                data-testid="dispute-freeze-test-btn"
                title="Tests renter dispute flow: 48h window, freeze execution, admin review, resolution (approve/cancel)"
              >
                {disputeFreezeTesting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <AlertTriangle className="w-4 h-4 mr-2" />
                )}
                Dispute Sys
              </Button>
              <Button 
                onClick={runFinancialDashboardTest}
                disabled={financialDashboardTesting}
                className="bg-cyan-600 hover:bg-cyan-700 h-10 sm:h-12 text-xs sm:text-sm"
                data-testid="financial-dashboard-test-btn"
                title="Tests Financial Enforcement Dashboard: KPIs, filters, adjustments, exports, and reconciliation"
              >
                {financialDashboardTesting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <DollarSign className="w-4 h-4 mr-2" />
                )}
                Fin Dashboard
              </Button>
            </div>

            {/* E2E Full Lifecycle Test - Featured */}
            <div className="mt-4 p-4 bg-gradient-to-r from-violet-900/40 to-indigo-900/40 border border-violet-500/50 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <Zap className="w-6 h-6 text-violet-400" />
                <div>
                  <h3 className="font-semibold text-violet-300">Part 8: End-to-End Full Lifecycle Test</h3>
                  <p className="text-xs text-slate-400">Validates the entire post-hire charge enforcement system (Parts 1–7)</p>
                </div>
              </div>
              <Button 
                onClick={runE2eLifecycleTest}
                disabled={e2eLifecycleTesting}
                className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 h-12 text-sm font-medium"
                data-testid="e2e-lifecycle-test-btn"
                title="Comprehensive test covering: Payment Auth, Charges Engine, Admin Review, Stripe Execution, Retry/Escalation, Disputes, and Financial Dashboard"
              >
                {e2eLifecycleTesting ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Running E2E...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5 mr-2" />
                    E2E Test (1-7)
                  </>
                )}
              </Button>
            </div>

            {/* P0 Overdue Protection System Test - Featured */}
            <div className="mt-4 p-4 bg-gradient-to-r from-amber-900/40 to-orange-900/40 border border-amber-500/50 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <AlertTriangle className="w-6 h-6 text-amber-400" />
                <div>
                  <h3 className="font-semibold text-amber-300">P0: Overdue Protection System</h3>
                  <p className="text-xs text-slate-400">Tests GRACE → OVERDUE state transitions, notifications, audit logs, and resolution workflows</p>
                </div>
              </div>
              <Button 
                onClick={runOverdueProtectionTest}
                disabled={overdueTesting}
                className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 h-12 text-sm font-medium"
                data-testid="overdue-protection-test-btn"
                title="Tests overdue detection, grace period, notifications, and resolution"
              >
                {overdueTesting ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Running Overdue...
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    Overdue Test
                  </>
                )}
              </Button>
            </div>

            {/* P0 Legal Re-Acceptance System Test - Featured */}
            <div className="mt-4 p-4 bg-gradient-to-r from-purple-900/40 to-indigo-900/40 border border-purple-500/50 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <FileText className="w-6 h-6 text-purple-400" />
                <div>
                  <h3 className="font-semibold text-purple-300">P0: Legal Re-Acceptance System</h3>
                  <p className="text-xs text-slate-400">Tests: publish new version → booking fails → accept terms → booking succeeds → audit log exists</p>
                </div>
              </div>
              <Button 
                onClick={runLegalReacceptanceTest}
                disabled={legalReacceptanceTesting}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 h-12 text-sm font-medium"
                data-testid="legal-reacceptance-test-btn"
                title="Tests legal compliance enforcement on login and booking creation"
              >
                {legalReacceptanceTesting ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Running Legal...
                  </>
                ) : (
                  <>
                    <FileText className="w-5 h-5 mr-2" />
                    Legal Test
                  </>
                )}
              </Button>
            </div>

            {/* P0 Test Results Display */}
            {(regoTestResults || cancellationTestResults || disputeTestResults || storedPaymentAuthResults || postHireChargesResults || adminReviewResults || stripeChargeResults || retryEscalationResults || disputeFreezeResults || financialDashboardResults || e2eLifecycleResults || overdueResults || legalReacceptanceResults) && (
              <div className="mt-4 space-y-3">
                {regoTestResults && (
                  <div className={`p-3 rounded-lg ${regoTestResults.all_passed ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {regoTestResults.all_passed ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                      <span className="font-medium text-sm">Rego Expiry Enforcement</span>
                    </div>
                    {regoTestResults.tests?.map((t, i) => (
                      <div key={i} className="text-xs text-white/70 flex items-center gap-2">
                        {t.passed ? <CheckCircle className="w-3 h-3 text-green-400" /> : <XCircle className="w-3 h-3 text-red-400" />}
                        {t.name}: {t.details}
                      </div>
                    ))}
                  </div>
                )}
                {cancellationTestResults && (
                  <div className={`p-3 rounded-lg ${cancellationTestResults.all_passed ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {cancellationTestResults.all_passed ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                      <span className="font-medium text-sm">Cancellation Policy Engine</span>
                    </div>
                    {cancellationTestResults.tests?.map((t, i) => (
                      <div key={i} className="text-xs text-white/70 flex items-center gap-2">
                        {t.passed ? <CheckCircle className="w-3 h-3 text-green-400" /> : <XCircle className="w-3 h-3 text-red-400" />}
                        {t.name}: {t.details}
                      </div>
                    ))}
                  </div>
                )}
                {disputeTestResults && (
                  <div className={`p-3 rounded-lg ${disputeTestResults.all_passed ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {disputeTestResults.all_passed ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                      <span className="font-medium text-sm">Dispute Workflow System</span>
                    </div>
                    {disputeTestResults.tests?.map((t, i) => (
                      <div key={i} className="text-xs text-white/70 flex items-center gap-2">
                        {t.passed ? <CheckCircle className="w-3 h-3 text-green-400" /> : <XCircle className="w-3 h-3 text-red-400" />}
                        {t.name}: {t.details}
                      </div>
                    ))}
                  </div>
                )}
                {storedPaymentAuthResults && (
                  <div className={`p-3 rounded-lg ${storedPaymentAuthResults.all_passed ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {storedPaymentAuthResults.all_passed ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                      <span className="font-medium text-sm">Stored Payment Authorization</span>
                      <span className="text-xs text-slate-400">
                        ({storedPaymentAuthResults.summary?.passed || 0}/{storedPaymentAuthResults.summary?.total || 0} passed)
                      </span>
                    </div>
                    {storedPaymentAuthResults.tests?.map((t, i) => (
                      <div key={i} className="text-xs text-white/70 flex items-center gap-2">
                        {t.passed ? <CheckCircle className="w-3 h-3 text-green-400" /> : <XCircle className="w-3 h-3 text-red-400" />}
                        {t.name}: {t.details}
                      </div>
                    ))}
                  </div>
                )}
                {postHireChargesResults && (
                  <div className={`p-3 rounded-lg ${postHireChargesResults.all_passed ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {postHireChargesResults.all_passed ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                      <span className="font-medium text-sm">Post-Hire Additional Charges</span>
                      <span className="text-xs text-slate-400">
                        ({postHireChargesResults.summary?.passed || 0}/{postHireChargesResults.summary?.total || 0} passed)
                      </span>
                    </div>
                    {postHireChargesResults.tests?.map((t, i) => (
                      <div key={i} className="text-xs text-white/70 flex items-center gap-2">
                        {t.passed ? <CheckCircle className="w-3 h-3 text-green-400" /> : <XCircle className="w-3 h-3 text-red-400" />}
                        {t.name}: {t.details}
                      </div>
                    ))}
                  </div>
                )}
                {adminReviewResults && (
                  <div className={`p-3 rounded-lg ${adminReviewResults.all_passed ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {adminReviewResults.all_passed ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                      <span className="font-medium text-sm">
                        Admin Review Layer (Part 3)
                        ({adminReviewResults.summary?.passed || 0}/{adminReviewResults.summary?.total || 0} passed)
                      </span>
                    </div>
                    {adminReviewResults.tests?.map((t, i) => (
                      <div key={i} className="text-xs text-white/70 flex items-center gap-2">
                        {t.passed ? <CheckCircle className="w-3 h-3 text-green-400" /> : <XCircle className="w-3 h-3 text-red-400" />}
                        {t.name}: {t.details}
                      </div>
                    ))}
                  </div>
                )}
                {stripeChargeResults && (
                  <div className={`p-3 rounded-lg ${stripeChargeResults.all_passed ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {stripeChargeResults.all_passed ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                      <span className="font-medium text-sm">
                        Stripe Charge Execution (Part 4)
                        ({stripeChargeResults.summary?.passed || 0}/{stripeChargeResults.summary?.total || 0} passed)
                      </span>
                    </div>
                    {stripeChargeResults.tests?.map((t, i) => (
                      <div key={i} className="text-xs text-white/70 flex items-center gap-2">
                        {t.passed ? <CheckCircle className="w-3 h-3 text-green-400" /> : 
                         t.skipped ? <span className="w-3 h-3 text-yellow-400">⊘</span> :
                         <XCircle className="w-3 h-3 text-red-400" />}
                        {t.name}: {t.details}
                      </div>
                    ))}
                    {stripeChargeResults.proof_summary && (
                      <div className="mt-3 p-2 bg-slate-900/50 rounded text-xs text-slate-300">
                        <p className="font-medium text-green-400 mb-1">Proof Summary:</p>
                        <p>Customer: {stripeChargeResults.proof_summary.stripe_customer_created}</p>
                        <p>PaymentIntent: {stripeChargeResults.proof_summary.payment_intent_created}</p>
                        <p>Status: {stripeChargeResults.proof_summary.payment_status}</p>
                        <p>Amount: ${(stripeChargeResults.proof_summary.amount_charged_cents || 0) / 100}</p>
                        <p>Idempotency: {stripeChargeResults.proof_summary.idempotency_verified ? '✅ Verified' : '❌ Not verified'}</p>
                        <p>Real Stripe: {stripeChargeResults.proof_summary.is_real_stripe_test_mode ? '✅ Yes' : '❌ No (mocked)'}</p>
                      </div>
                    )}
                  </div>
                )}
                {retryEscalationResults && (
                  <div className={`p-3 rounded-lg ${retryEscalationResults.all_passed ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {retryEscalationResults.all_passed ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                      <span className="font-medium text-sm">
                        Retry & Escalation (Part 5)
                        ({retryEscalationResults.summary?.passed || 0}/{retryEscalationResults.summary?.total || 0} passed)
                      </span>
                    </div>
                    {retryEscalationResults.tests?.map((t, i) => (
                      <div key={i} className="text-xs text-white/70 flex items-center gap-2">
                        {t.passed ? <CheckCircle className="w-3 h-3 text-green-400" /> : 
                         t.skipped ? <span className="w-3 h-3 text-yellow-400">⊘</span> :
                         <XCircle className="w-3 h-3 text-red-400" />}
                        {t.name}: {t.details?.slice(0, 80)}
                      </div>
                    ))}
                    {retryEscalationResults.proof_summary && (
                      <div className="mt-3 p-2 bg-slate-900/50 rounded text-xs text-slate-300">
                        <p className="font-medium text-amber-400 mb-1">Proof Summary:</p>
                        <p>Decline card used: {retryEscalationResults.proof_summary.declining_payment_method ? '✅' : '❌'}</p>
                        <p>Retry triggered: {retryEscalationResults.proof_summary.initial_failure_triggered_retry ? '✅' : '❌'}</p>
                        <p>Status transitions: {retryEscalationResults.proof_summary.collection_status_transitions}</p>
                        <p>User suspended: {retryEscalationResults.proof_summary.user_suspension_applied ? '✅' : '❌'}</p>
                        <p>Freeze/Unfreeze: {retryEscalationResults.proof_summary.freeze_unfreeze_worked ? '✅' : '❌'}</p>
                        <p>Audit logs: {retryEscalationResults.proof_summary.audit_logs_created}</p>
                        <p>Notifications: {retryEscalationResults.proof_summary.notifications_sent}</p>
                      </div>
                    )}
                  </div>
                )}
                {disputeFreezeResults && (
                  <div className={`p-3 rounded-lg ${disputeFreezeResults.all_passed ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {disputeFreezeResults.all_passed ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                      <span className="font-medium text-sm">
                        Dispute Interaction System (Part 6)
                        ({disputeFreezeResults.summary?.passed || 0}/{disputeFreezeResults.summary?.total || 0} passed)
                      </span>
                    </div>
                    {disputeFreezeResults.tests?.map((t, i) => (
                      <div key={i} className="text-xs text-white/70 flex items-center gap-2">
                        {t.passed ? <CheckCircle className="w-3 h-3 text-green-400" /> : 
                         t.skipped ? <span className="w-3 h-3 text-yellow-400">⊘</span> :
                         <XCircle className="w-3 h-3 text-red-400" />}
                        {t.name}: {t.details?.slice(0, 80)}
                      </div>
                    ))}
                    {disputeFreezeResults.proof_summary && (
                      <div className="mt-3 p-2 bg-slate-900/50 rounded text-xs text-slate-300">
                        <p className="font-medium text-pink-400 mb-1">Proof Summary:</p>
                        <p>48h window works: {disputeFreezeResults.proof_summary.dispute_window_working ? '✅' : '❌'}</p>
                        <p>Charge frozen on dispute: {disputeFreezeResults.proof_summary.charge_frozen_on_dispute ? '✅' : '❌'}</p>
                        <p>Retries blocked: {disputeFreezeResults.proof_summary.retries_blocked ? '✅' : '❌'}</p>
                        <p>Investigation status: {disputeFreezeResults.proof_summary.investigation_status_updated ? '✅' : '❌'}</p>
                        <p>Approve resolution: {disputeFreezeResults.proof_summary.resolution_approve_worked ? '✅' : '❌'}</p>
                        <p>Charge unfrozen: {disputeFreezeResults.proof_summary.charge_unfrozen_after_resolve ? '✅' : '❌'}</p>
                        <p>Cancellation works: {disputeFreezeResults.proof_summary.cancellation_worked ? '✅' : '❌'}</p>
                        <p>Audit logs: {disputeFreezeResults.proof_summary.audit_logs_created}</p>
                      </div>
                    )}
                  </div>
                )}
                {financialDashboardResults && (
                  <div className={`p-3 rounded-lg ${financialDashboardResults.all_passed ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {financialDashboardResults.all_passed ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                      <span className="font-medium text-sm">
                        Financial Enforcement Dashboard (Part 7)
                        ({financialDashboardResults.summary?.passed || 0}/{financialDashboardResults.summary?.total || 0} passed)
                      </span>
                    </div>
                    {financialDashboardResults.tests?.map((t, i) => (
                      <div key={i} className="text-xs text-white/70 flex items-center gap-2">
                        {t.passed ? <CheckCircle className="w-3 h-3 text-green-400" /> : 
                         t.skipped ? <span className="w-3 h-3 text-yellow-400">⊘</span> :
                         <XCircle className="w-3 h-3 text-red-400" />}
                        {t.name}: {t.details?.slice(0, 80)}
                      </div>
                    ))}
                    {financialDashboardResults.proof_summary && (
                      <div className="mt-3 p-2 bg-slate-900/50 rounded text-xs text-slate-300">
                        <p className="font-medium text-cyan-400 mb-1">Proof Summary:</p>
                        <p>Charges seeded: {financialDashboardResults.proof_summary.charges_seeded}</p>
                        <p>Dashboard KPIs: {financialDashboardResults.proof_summary.dashboard_kpis_work ? '✅' : '❌'}</p>
                        <p>Filters work: {financialDashboardResults.proof_summary.filters_work ? '✅' : '❌'}</p>
                        <p>Charge detail: {financialDashboardResults.proof_summary.charge_detail_works ? '✅' : '❌'}</p>
                        <p>Adjustments: {financialDashboardResults.proof_summary.adjustment_works ? '✅' : '❌'}</p>
                        <p>Collections: {financialDashboardResults.proof_summary.collections_found}</p>
                        <p>Disputes: {financialDashboardResults.proof_summary.disputes_found}</p>
                        <p>Audit logs: {financialDashboardResults.proof_summary.audit_logs_work ? '✅' : '❌'}</p>
                        <p>Exports: {financialDashboardResults.proof_summary.exports_work ? '✅' : '❌'}</p>
                      </div>
                    )}
                  </div>
                )}
                {/* E2E Full Lifecycle Results - Full Width Card */}
                {e2eLifecycleResults && (
                  <div className={`col-span-full p-4 rounded-lg ${e2eLifecycleResults.all_passed ? 'bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/50' : 'bg-gradient-to-r from-red-900/30 to-orange-900/30 border border-red-500/50'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {e2eLifecycleResults.all_passed ? 
                          <CheckCircle className="w-6 h-6 text-green-400" /> : 
                          <XCircle className="w-6 h-6 text-red-400" />
                        }
                        <div>
                          <span className="font-semibold text-base">
                            E2E Full Lifecycle Test (Parts 1-7)
                          </span>
                          <span className="ml-2 text-sm text-slate-400">
                            ({e2eLifecycleResults.summary?.passed || 0}/{e2eLifecycleResults.summary?.total || 0} passed)
                          </span>
                          {e2eLifecycleResults.test_id && (
                            <p className="text-xs text-slate-500 font-mono mt-1">ID: {e2eLifecycleResults.test_id}</p>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyE2eReport}
                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        data-testid="copy-e2e-report-btn"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Report
                      </Button>
                    </div>
                    
                    {/* Step-by-step results */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
                      {e2eLifecycleResults.tests?.map((t, i) => (
                        <div key={i} className="text-xs text-white/80 flex items-start gap-2 p-2 bg-slate-900/40 rounded">
                          {t.passed ? <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" /> : 
                           t.skipped ? <span className="w-3 h-3 text-yellow-400 flex-shrink-0 mt-0.5">⊘</span> :
                           <XCircle className="w-3 h-3 text-red-400 flex-shrink-0 mt-0.5" />}
                          <div>
                            <span className="font-medium">{t.name}</span>
                            <p className="text-slate-400 text-[10px] mt-0.5">{t.details?.slice(0, 60)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Key IDs Section */}
                    {e2eLifecycleResults.key_ids && (
                      <div className="mb-4 p-3 bg-slate-900/50 rounded">
                        <p className="font-medium text-violet-400 mb-2 text-sm">Key IDs Created:</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs font-mono">
                          <div>
                            <span className="text-slate-500">Booking:</span>
                            <p className="text-cyan-400 truncate">{e2eLifecycleResults.key_ids.booking_id?.slice(0, 16)}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Charge:</span>
                            <p className="text-cyan-400 truncate">{e2eLifecycleResults.key_ids.charge_id?.slice(0, 16)}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Dispute:</span>
                            <p className="text-cyan-400 truncate">{e2eLifecycleResults.key_ids.dispute_id?.slice(0, 16) || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">PaymentIntent:</span>
                            <p className="text-cyan-400 truncate">{e2eLifecycleResults.key_ids.payment_intent_id?.slice(0, 16) || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Proof Summary */}
                    {e2eLifecycleResults.proof_summary && (
                      <div className="mb-4 p-3 bg-slate-900/50 rounded">
                        <p className="font-medium text-violet-400 mb-2 text-sm">Proof Summary:</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                          <div>
                            <span className="text-slate-500">Stripe:</span>
                            <p>{e2eLifecycleResults.proof_summary.stripe_configured ? '✅ Configured' : '⚠️ Disabled'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Payment:</span>
                            <p>{e2eLifecycleResults.proof_summary.payment_executed ? '✅ Executed' : '⏸️ N/A'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Idempotency:</span>
                            <p>{e2eLifecycleResults.proof_summary.idempotency_verified ? '✅ Verified' : '⏸️ N/A'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Revision Track:</span>
                            <p>{e2eLifecycleResults.proof_summary.revision_tracking ? '✅ Works' : '❌ Failed'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">User Suspend:</span>
                            <p>{e2eLifecycleResults.proof_summary.user_suspension ? '✅ Works' : '❌ Failed'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Dispute Freeze:</span>
                            <p>{e2eLifecycleResults.proof_summary.dispute_freeze ? '✅ Works' : '❌ Failed'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">CSV Exports:</span>
                            <p>{e2eLifecycleResults.proof_summary.csv_exports_valid ? '✅ Valid' : '❌ Failed'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Audit Events:</span>
                            <p>{e2eLifecycleResults.proof_summary.audit_events_created || 0} created</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Audit Event Counts */}
                    {e2eLifecycleResults.audit_event_counts && (
                      <div className="mb-4 p-3 bg-slate-900/50 rounded">
                        <p className="font-medium text-violet-400 mb-2 text-sm">Audit Events: {e2eLifecycleResults.audit_event_counts.total}</p>
                        <div className="flex flex-wrap gap-2">
                          {e2eLifecycleResults.audit_event_counts.sample_types?.map((type, i) => (
                            <Badge key={i} variant="outline" className="text-[10px] border-slate-600 text-slate-400">
                              {type}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2 mt-4">
                      <a 
                        href="/financial-enforcement" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 text-xs bg-violet-600/20 border border-violet-500/50 rounded text-violet-300 hover:bg-violet-600/30"
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        Open Financial Dashboard
                      </a>
                      <a 
                        href="/financial-enforcement?tab=audit" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 text-xs bg-indigo-600/20 border border-indigo-500/50 rounded text-indigo-300 hover:bg-indigo-600/30"
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        View Audit Logs
                      </a>
                      <a 
                        href="/financial-enforcement?tab=collections" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 text-xs bg-cyan-600/20 border border-cyan-500/50 rounded text-cyan-300 hover:bg-cyan-600/30"
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        View Collections
                      </a>
                    </div>
                    
                    {/* Cleanup Status */}
                    {e2eLifecycleResults.cleanup && (
                      <div className="mt-3 pt-3 border-t border-slate-700 text-xs text-slate-400">
                        <span className="font-medium">Cleanup:</span>
                        <span className={e2eLifecycleResults.cleanup.success ? ' text-green-400' : ' text-yellow-400'}>
                          {e2eLifecycleResults.cleanup.success ? ' ✅ All test data deleted' : ' ⚠️ Some cleanup errors'}
                        </span>
                        {e2eLifecycleResults.summary?.duration_seconds && (
                          <span className="ml-4 text-slate-500">
                            Duration: {e2eLifecycleResults.summary.duration_seconds.toFixed(1)}s
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {/* Overdue Protection Results */}
                {overdueResults && (
                  <div className={`col-span-full p-4 rounded-lg ${overdueResults.all_passed ? 'bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/50' : 'bg-gradient-to-r from-red-900/30 to-orange-900/30 border border-red-500/50'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {overdueResults.all_passed ? 
                          <CheckCircle className="w-6 h-6 text-green-400" /> : 
                          <XCircle className="w-6 h-6 text-red-400" />
                        }
                        <div>
                          <span className="font-semibold text-base">
                            Overdue Protection System Test
                          </span>
                          <span className="ml-2 text-sm text-slate-400">
                            ({overdueResults.summary?.passed || 0}/{overdueResults.summary?.total || 0} passed)
                          </span>
                          {overdueResults.test_id && (
                            <p className="text-xs text-slate-500 font-mono mt-1">ID: {overdueResults.test_id}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Step-by-step results */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                      {overdueResults.tests?.map((t, i) => (
                        <div key={i} className="text-xs text-white/80 flex items-start gap-2 p-2 bg-slate-900/40 rounded">
                          {t.passed ? <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" /> : 
                           <XCircle className="w-3 h-3 text-red-400 flex-shrink-0 mt-0.5" />}
                          <div>
                            <span className="font-medium">{t.name}</span>
                            <p className="text-slate-400 text-[10px] mt-0.5">{t.details?.slice(0, 80)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Proof Summary */}
                    {overdueResults.proof_summary && (
                      <div className="mb-4 p-3 bg-slate-900/50 rounded">
                        <p className="font-medium text-amber-400 mb-2 text-sm">Proof Summary:</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                          <div>
                            <span className="text-slate-500">GRACE State:</span>
                            <p>{overdueResults.proof_summary.grace_state_works ? '✅ Works' : '❌ Failed'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">OVERDUE State:</span>
                            <p>{overdueResults.proof_summary.overdue_state_works ? '✅ Works' : '❌ Failed'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Notifications:</span>
                            <p>{overdueResults.proof_summary.notifications_sent ? '✅ Sent' : '❌ Failed'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Audit Logged:</span>
                            <p>{overdueResults.proof_summary.audit_logged ? '✅ Yes' : '❌ No'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">System Resolve:</span>
                            <p>{overdueResults.proof_summary.system_resolve_works ? '✅ Works' : '❌ Failed'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Manual Resolve:</span>
                            <p>{overdueResults.proof_summary.manual_resolve_works ? '✅ Works' : '❌ Failed'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Reason in Audit:</span>
                            <p>{overdueResults.proof_summary.reason_in_audit ? '✅ Yes' : '❌ No'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Cleanup:</span>
                            <p>{overdueResults.proof_summary.cleanup_success ? '✅ Success' : '⚠️ Errors'}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Key IDs */}
                    {overdueResults.key_ids && (
                      <div className="p-3 bg-slate-900/50 rounded text-xs">
                        <p className="font-medium text-amber-400 mb-2">Key IDs Created:</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 font-mono">
                          <div>
                            <span className="text-slate-500">Grace Booking:</span>
                            <p className="text-cyan-400 truncate">{overdueResults.key_ids.booking_grace_id?.slice(0, 16)}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Overdue Booking:</span>
                            <p className="text-cyan-400 truncate">{overdueResults.key_ids.booking_overdue_id?.slice(0, 16)}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Resolved Booking:</span>
                            <p className="text-cyan-400 truncate">{overdueResults.key_ids.booking_resolve_id?.slice(0, 16)}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Manual Resolve:</span>
                            <p className="text-cyan-400 truncate">{overdueResults.key_ids.booking_manual_id?.slice(0, 16)}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Cleanup Status */}
                    {overdueResults.cleanup && (
                      <div className="mt-3 pt-3 border-t border-slate-700 text-xs text-slate-400">
                        <span className="font-medium">Cleanup:</span>
                        <span className={overdueResults.cleanup.success ? ' text-green-400' : ' text-yellow-400'}>
                          {overdueResults.cleanup.success ? ' ✅ All test data deleted' : ' ⚠️ Some cleanup errors'}
                        </span>
                        {overdueResults.audit_event_count && (
                          <span className="ml-4 text-slate-500">
                            Audit events: {overdueResults.audit_event_count}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Legal Re-Acceptance System Results - Full Width Card */}
                {legalReacceptanceResults && (
                  <div className={`col-span-full p-4 rounded-lg ${legalReacceptanceResults.all_passed ? 'bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/50' : 'bg-gradient-to-r from-red-900/30 to-orange-900/30 border border-red-500/50'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {legalReacceptanceResults.all_passed ? 
                          <CheckCircle className="w-6 h-6 text-green-400" /> : 
                          <XCircle className="w-6 h-6 text-red-400" />
                        }
                        <div>
                          <span className="font-semibold text-base">
                            Legal Re-Acceptance System Test
                          </span>
                          <span className="ml-2 text-sm text-slate-400">
                            ({legalReacceptanceResults.summary?.passed || 0}/{legalReacceptanceResults.summary?.total || 0} passed)
                          </span>
                          {legalReacceptanceResults.test_id && (
                            <p className="text-xs text-slate-500 font-mono mt-1">ID: {legalReacceptanceResults.test_id}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Step-by-step results */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                      {legalReacceptanceResults.tests?.map((t, i) => (
                        <div key={i} className="text-xs text-white/80 flex items-start gap-2 p-2 bg-slate-900/40 rounded">
                          {t.passed ? <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" /> : 
                           <XCircle className="w-3 h-3 text-red-400 flex-shrink-0 mt-0.5" />}
                          <div>
                            <span className="font-medium">{t.name}</span>
                            <p className="text-slate-400 text-[10px] mt-0.5">{t.details?.slice(0, 80)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Proof Summary */}
                    {legalReacceptanceResults.proof_summary && (
                      <div className="mb-4 p-3 bg-slate-900/50 rounded">
                        <p className="font-medium text-purple-400 mb-2 text-sm">Proof Summary:</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                          <div>
                            <span className="text-slate-500">Compliance Check:</span>
                            <p>{legalReacceptanceResults.proof_summary.compliance_check_works ? '✅ Works' : '❌ Failed'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Booking Blocked (Before):</span>
                            <p>{legalReacceptanceResults.proof_summary.booking_blocked_before ? '✅ Yes' : '❌ No'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Acceptance Stored:</span>
                            <p>{legalReacceptanceResults.proof_summary.acceptance_stored ? '✅ Yes' : '❌ No'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Booking Allowed (After):</span>
                            <p>{legalReacceptanceResults.proof_summary.booking_allowed_after ? '✅ Yes' : '❌ No'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Audit Log Exists:</span>
                            <p>{legalReacceptanceResults.proof_summary.audit_log_exists ? '✅ Yes' : '❌ No'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Audit Has Versions:</span>
                            <p>{legalReacceptanceResults.proof_summary.audit_has_versions ? '✅ Yes' : '❌ No'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Re-Accept After New Publish:</span>
                            <p>{legalReacceptanceResults.proof_summary.reaccept_after_new_publish ? '✅ Yes' : '❌ No'}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Cleanup:</span>
                            <p>{legalReacceptanceResults.proof_summary.cleanup_success ? '✅ Success' : '⚠️ Errors'}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Key IDs */}
                    {legalReacceptanceResults.key_ids && (
                      <div className="p-3 bg-slate-900/50 rounded text-xs">
                        <p className="font-medium text-purple-400 mb-2">Key IDs Created:</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 font-mono">
                          <div>
                            <span className="text-slate-500">Owner:</span>
                            <p className="text-cyan-400 truncate">{legalReacceptanceResults.key_ids.owner_id?.slice(0, 20)}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Renter:</span>
                            <p className="text-cyan-400 truncate">{legalReacceptanceResults.key_ids.renter_id?.slice(0, 20)}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Terms Version:</span>
                            <p className="text-cyan-400 truncate">{legalReacceptanceResults.key_ids.terms_version}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Privacy Version:</span>
                            <p className="text-cyan-400 truncate">{legalReacceptanceResults.key_ids.privacy_version}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Cleanup Status */}
                    {legalReacceptanceResults.cleanup && (
                      <div className="mt-3 pt-3 border-t border-slate-700 text-xs text-slate-400">
                        <span className="font-medium">Cleanup:</span>
                        <span className={legalReacceptanceResults.cleanup.success ? ' text-green-400' : ' text-yellow-400'}>
                          {legalReacceptanceResults.cleanup.success ? ' ✅ All test data deleted & legal pages restored' : ' ⚠️ Some cleanup errors'}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* P1: Extension Flow System Test */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="font-heading flex items-center gap-3 text-sm sm:text-base">
              <RefreshCw className="w-5 h-5 text-purple-400" />
              Extension Flow System
            </CardTitle>
            <CardDescription className="text-slate-400 text-xs sm:text-sm">
              Test renter extension request → Owner approve → Stripe charge → Booking update
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
            <Button
              onClick={runExtensionFlowTest}
              disabled={extensionFlowTesting}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="run-extension-flow-test-btn"
            >
              {extensionFlowTesting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Running Extension...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Extension Test
                </>
              )}
            </Button>

            {extensionFlowResults && (
              <div className="mt-4">
                <div className={`p-3 rounded-lg border ${
                  extensionFlowResults.all_passed 
                    ? 'bg-green-500/10 border-green-500/30' 
                    : 'bg-red-500/10 border-red-500/30'
                }`}>
                  <div className="flex items-center gap-2 mb-3">
                    {extensionFlowResults.all_passed ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                    )}
                    <span className="font-medium">
                      {extensionFlowResults.all_passed ? 'All Tests Passed' : 'Some Tests Failed or Skipped'}
                    </span>
                    <Badge className={extensionFlowResults.all_passed ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}>
                      {extensionFlowResults.summary?.passed || 0}/{extensionFlowResults.summary?.total || 0}
                    </Badge>
                  </div>

                  {/* Test Steps */}
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {extensionFlowResults.tests?.map((test, idx) => (
                      <div 
                        key={idx} 
                        className={`flex items-start gap-2 text-xs p-2 rounded ${
                          test.passed ? 'bg-green-500/10' : test.skipped ? 'bg-slate-500/10' : 'bg-red-500/10'
                        }`}
                      >
                        {test.passed ? (
                          <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                        ) : test.skipped ? (
                          <AlertTriangle className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                        )}
                        <div>
                          <span className="font-medium">{test.name}</span>
                          {test.details && (
                            <p className="text-slate-400 text-[10px] mt-0.5 break-words">{test.details}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Proof Summary */}
                  {extensionFlowResults.proof_summary && (
                    <div className="mt-3 pt-3 border-t border-slate-700">
                      <span className="text-xs font-medium text-slate-400 block mb-2">Proof Summary:</span>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Extension Created:</span>
                          <span>{extensionFlowResults.proof_summary.extension_created ? '✅' : '❌'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Conflict Detection:</span>
                          <span>{extensionFlowResults.proof_summary.conflict_detection ? '✅' : '❌'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Stripe Configured:</span>
                          <span>{extensionFlowResults.proof_summary.stripe_configured ? '✅' : '⚠️'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Booking Update Logic:</span>
                          <span>{extensionFlowResults.proof_summary.booking_updated_correctly ? '✅' : '❌'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Payment Failed Handling:</span>
                          <span>{extensionFlowResults.proof_summary.payment_failed_handled ? '✅' : '⚠️'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Cancellation Works:</span>
                          <span>{extensionFlowResults.proof_summary.cancellation_works ? '✅' : '❌'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Rejection Works:</span>
                          <span>{extensionFlowResults.proof_summary.rejection_works ? '✅' : '❌'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Cleanup:</span>
                          <span>{extensionFlowResults.proof_summary.cleanup_success ? '✅' : '⚠️'}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Key IDs */}
                  {extensionFlowResults.key_ids && (
                    <div className="mt-3 pt-3 border-t border-slate-700 text-xs">
                      <span className="font-medium text-slate-400">Test IDs:</span>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <span className="text-slate-500">Extension:</span>
                          <span className="text-cyan-400 ml-1 truncate block">{extensionFlowResults.key_ids.extension_id?.slice(0, 15)}...</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Booking:</span>
                          <span className="text-cyan-400 ml-1 truncate block">{extensionFlowResults.key_ids.booking_id?.slice(0, 15)}...</span>
                        </div>
                        {extensionFlowResults.key_ids.payment_intent_id && (
                          <div className="col-span-2">
                            <span className="text-slate-500">Payment Intent:</span>
                            <span className="text-green-400 ml-1 truncate block">{extensionFlowResults.key_ids.payment_intent_id}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Test Results */}
        {testResults && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="font-heading flex items-center gap-3 text-sm sm:text-base">
                <Bug className="w-5 h-5 text-green-400" />
                Self Test Results
                <Badge className={testResults.summary.failed === 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                  {testResults.summary.passed}/{testResults.summary.total} Passed
                </Badge>
              </CardTitle>
              <CardDescription className="text-slate-400 text-xs sm:text-sm">
                Run at: {new Date(testResults.run_at).toLocaleString()}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
              <div className="space-y-2 sm:space-y-3">
                {testResults.tests.map((test, index) => (
                  <div 
                    key={index} 
                    className={`p-3 sm:p-4 rounded-lg border ${
                      test.status === 'passed' 
                        ? 'bg-green-500/10 border-green-500/30' 
                        : 'bg-red-500/10 border-red-500/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 sm:gap-3">
                        {test.status === 'passed' ? (
                          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-400 flex-shrink-0" />
                        )}
                        <span className="font-medium text-xs sm:text-sm">{test.name}</span>
                      </div>
                      <Badge className={`text-[10px] sm:text-xs ${test.status === 'passed' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {test.status}
                      </Badge>
                    </div>
                    <p className="text-[10px] sm:text-sm text-slate-400 mt-1 sm:mt-2 ml-6 sm:ml-8 break-words">{test.details}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Flow Test Results */}
        {paymentTestResults && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="font-heading flex items-center gap-3 text-sm sm:text-base">
                <CreditCard className="w-5 h-5 text-purple-400" />
                Payment Flow Test
                <Badge className={paymentTestResults.summary?.success ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                  {paymentTestResults.summary?.passed}/{paymentTestResults.summary?.total} Passed
                </Badge>
              </CardTitle>
              <CardDescription className="text-slate-400 text-xs sm:text-sm">
                Test ID: {paymentTestResults.test_id}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
              <div className="space-y-2 sm:space-y-3">
                {paymentTestResults.steps?.map((step, index) => (
                  <div 
                    key={index} 
                    className={`p-3 sm:p-4 rounded-lg border ${
                      step.status === 'passed' 
                        ? 'bg-green-500/10 border-green-500/30' 
                        : 'bg-red-500/10 border-red-500/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 sm:gap-3">
                        {step.status === 'passed' ? (
                          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-400 flex-shrink-0" />
                        )}
                        <span className="font-medium text-xs sm:text-sm">{step.step}</span>
                      </div>
                      <Badge className={`text-[10px] sm:text-xs ${step.status === 'passed' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {step.status}
                      </Badge>
                    </div>
                    <p className="text-[10px] sm:text-sm text-slate-400 mt-1 sm:mt-2 ml-6 sm:ml-8 break-words">{step.details}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Verification Smoke Test Results */}
        {verificationTestResults && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="font-heading flex items-center gap-3 text-sm sm:text-base">
                <Users className="w-5 h-5 text-orange-400" />
                Verification Smoke Test
                <Badge className="bg-orange-500/20 text-orange-400">
                  {verificationTestResults.total_users} users
                </Badge>
              </CardTitle>
              <CardDescription className="text-slate-400 text-xs sm:text-sm">
                Fetched at: {new Date(verificationTestResults.fetched_at).toLocaleString()}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0 space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-slate-900 rounded-lg border border-slate-700 text-center">
                  <p className="text-2xl font-bold text-white">{verificationTestResults.total_users}</p>
                  <p className="text-xs text-slate-400">Total Users</p>
                </div>
                <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30 text-center">
                  <p className="text-2xl font-bold text-yellow-400">{verificationTestResults.pending_verifications}</p>
                  <p className="text-xs text-slate-400">Pending</p>
                </div>
                <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30 text-center">
                  <p className="text-2xl font-bold text-green-400">{verificationTestResults.verified_users}</p>
                  <p className="text-xs text-slate-400">Verified</p>
                </div>
              </div>

              {/* First 3 Users */}
              <div>
                <p className="text-sm font-medium text-slate-300 mb-2">First 3 Users:</p>
                <div className="space-y-2">
                  {verificationTestResults.first_3_users?.map((u, i) => (
                    <div key={i} className="p-3 bg-slate-900 rounded-lg border border-slate-700 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{u.full_name}</p>
                        <p className="text-xs text-slate-400 font-mono">{u.email}</p>
                      </div>
                      <Badge className={`text-xs ${
                        u.verification_status === 'verified' ? 'bg-green-500/20 text-green-400' :
                        u.verification_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        u.verification_status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {u.verification_status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Verification Audit Logs */}
              <div>
                <p className="text-sm font-medium text-slate-300 mb-2">Last 10 Verification Audit Logs:</p>
                {verificationTestResults.verification_audit_logs?.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">No verification audit logs found</p>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {verificationTestResults.verification_audit_logs?.map((log, i) => (
                      <div key={i} className="p-2 bg-slate-900 rounded border border-slate-700 text-xs font-mono">
                        <div className="flex items-center justify-between mb-1">
                          <Badge variant="outline" className="text-[10px] border-slate-600">{log.action}</Badge>
                          <span className="text-slate-500">{new Date(log.created_at).toLocaleString()}</span>
                        </div>
                        <div className="text-slate-400">
                          <span className="text-cyan-400">{log.entity_type}</span>: 
                          <span className="text-yellow-400 ml-1">{log.entity_id?.slice(0, 20)}</span>
                        </div>
                        <div className="text-slate-500 mt-1">by {log.user_email}</div>
                        {log.metadata?.reason && (
                          <div className="text-red-400 mt-1">Reason: {log.metadata.reason}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Vehicle Visibility Test Results */}
        {vehicleVisibilityResults && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="font-heading flex items-center gap-3 text-sm sm:text-base">
                <Car className="w-5 h-5 text-blue-400" />
                Vehicle Visibility Test
                <Badge className="bg-blue-500/20 text-blue-400">
                  {vehicleVisibilityResults.public_vehicle_count} public vehicles
                </Badge>
              </CardTitle>
              <CardDescription className="text-slate-400 text-xs sm:text-sm">
                Fetched at: {new Date(vehicleVisibilityResults.fetched_at).toLocaleString()}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0 space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-900 rounded-lg border border-slate-700 text-center">
                  <p className="text-2xl font-bold text-white">{vehicleVisibilityResults.public_vehicle_count}</p>
                  <p className="text-xs text-slate-400">Public Vehicles (Browse)</p>
                </div>
                <div className="p-3 bg-slate-900 rounded-lg border border-slate-700 text-center">
                  <p className="text-2xl font-bold text-white">{vehicleVisibilityResults.my_vehicle_count}</p>
                  <p className="text-xs text-slate-400">My Vehicles (Owner)</p>
                </div>
              </div>

              {/* Last Created Vehicle */}
              {vehicleVisibilityResults.last_created_vehicle && (
                <div>
                  <p className="text-sm font-medium text-slate-300 mb-2">Last Created Vehicle:</p>
                  <div className="p-3 bg-slate-900 rounded-lg border border-slate-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-white">{vehicleVisibilityResults.last_created_vehicle.make_model}</span>
                      <Badge className={`text-xs ${
                        vehicleVisibilityResults.last_created_vehicle.status === 'active' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {vehicleVisibilityResults.last_created_vehicle.status}
                      </Badge>
                    </div>
                    <div className="text-xs text-slate-400 space-y-1">
                      <p><span className="text-cyan-400">Vehicle ID:</span> {vehicleVisibilityResults.last_created_vehicle.vehicle_id}</p>
                      <p><span className="text-cyan-400">Owner ID:</span> {vehicleVisibilityResults.last_created_vehicle.owner_id}</p>
                      <p><span className="text-cyan-400">Created:</span> {vehicleVisibilityResults.last_created_vehicle.created_at}</p>
                      <p><span className="text-cyan-400">Photos:</span> {vehicleVisibilityResults.last_created_vehicle.photos?.length || 0} valid photos</p>
                    </div>
                    <div className="mt-2 flex gap-2">
                      <Badge className={vehicleVisibilityResults.in_public_list ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                        {vehicleVisibilityResults.in_public_list ? '✓ In Browse List' : '✗ NOT in Browse List'}
                      </Badge>
                      <Badge className={vehicleVisibilityResults.in_my_list ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                        {vehicleVisibilityResults.in_my_list ? '✓ In My Vehicles' : '? Not in My Vehicles'}
                      </Badge>
                    </div>
                  </div>
                </div>
              )}

              {/* Photo URL Tests */}
              {vehicleVisibilityResults.photo_url_tests?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-300 mb-2">Photo URL Resolution Tests:</p>
                  <div className="space-y-2">
                    {vehicleVisibilityResults.photo_url_tests.map((test, i) => (
                      <div key={i} className="p-3 bg-slate-900 rounded-lg border border-slate-700">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-white text-sm">{test.make_model}</span>
                          <Badge className="bg-blue-500/20 text-blue-400 text-xs">
                            {test.photo_count} photos
                          </Badge>
                        </div>
                        <div className="text-xs text-slate-400 space-y-1 font-mono">
                          <p><span className="text-yellow-400">S3 Key:</span> {test.s3_key}</p>
                          <p><span className="text-green-400">URL:</span> {test.resolved_url}</p>
                        </div>
                        {/* Photo preview */}
                        <div className="mt-2 flex items-center gap-2">
                          <img 
                            src={test.resolved_url}
                            alt="Preview"
                            className="w-16 h-12 object-cover rounded"
                            onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23333" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23f66" font-size="12">FAIL</text></svg>'; }}
                          />
                          <span className="text-xs text-slate-500">Preview (check console for errors)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Event Log */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              Event Log (Last 100)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {eventLog.length === 0 ? (
              <p className="text-center py-8 text-slate-500">No events logged yet</p>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto font-mono text-sm">
                {eventLog.map((event, index) => (
                  <div key={index} className="p-3 bg-slate-900 rounded border border-slate-700">
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant="outline" className="text-xs border-slate-600">
                        {event.action}
                      </Badge>
                      <span className="text-xs text-slate-500">
                        {new Date(event.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-slate-400">
                      <span className="text-cyan-400">{event.entity_type}</span>
                      <span className="text-slate-600"> : </span>
                      <span className="text-yellow-400">{event.entity_id?.slice(0, 16)}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      by {event.user_email || 'system'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* System Info */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="font-heading">System Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4 font-mono text-sm">
              <div className="p-4 bg-slate-900 rounded">
                <p className="text-slate-500">API URL</p>
                <p className="text-green-400 break-all">{process.env.REACT_APP_BACKEND_URL}</p>
              </div>
              <div className="p-4 bg-slate-900 rounded">
                <p className="text-slate-500">Environment</p>
                <p className="text-yellow-400">{process.env.NODE_ENV}</p>
              </div>
              <div className="p-4 bg-slate-900 rounded">
                <p className="text-slate-500">Build Time</p>
                <p className="text-cyan-400">{new Date().toISOString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Dialog for Dangerous Actions */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}>
        <AlertDialogContent className="bg-slate-800 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className={confirmDialog.destructive ? 'text-red-400' : 'text-yellow-400'}>
              {confirmDialog.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              {confirmDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 text-white hover:bg-slate-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={confirmDialog.destructive 
                ? 'bg-red-600 hover:bg-red-700' 
                : 'bg-yellow-600 hover:bg-yellow-700'}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DevDebugPanel;
